import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { createChart, AreaSeries, LineSeries, HistogramSeries, ColorType } from "lightweight-charts";

function classToColor(cls) {
  if (!cls) return "#71717a";
  const c = cls.toLowerCase();
  if (c.includes("extreme fear")) return "#ef4444";
  if (c.includes("fear")) return "#f97316";
  if (c.includes("neutral")) return "#eab308";
  if (c.includes("greed") && c.includes("extreme")) return "#10b981";
  if (c.includes("greed")) return "#22c55e";
  return "#71717a";
}

function FearGreedGauge({ value, classification }) {
  const angle = (value / 100) * 180 - 90;
  const color = classToColor(classification);
  return (
    <div className="flex flex-col items-center" data-testid="fear-greed-gauge">
      <svg viewBox="0 0 200 110" className="w-full max-w-[180px]">
        {/* Background arc */}
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(63,63,70,0.3)" strokeWidth="10" strokeLinecap="round" />
        {/* Colored segments */}
        <path d="M 20 100 A 80 80 0 0 1 52 42" fill="none" stroke="#ef4444" strokeWidth="10" strokeLinecap="round" opacity="0.6" />
        <path d="M 52 42 A 80 80 0 0 1 100 20" fill="none" stroke="#f97316" strokeWidth="10" strokeLinecap="round" opacity="0.6" />
        <path d="M 100 20 A 80 80 0 0 1 148 42" fill="none" stroke="#eab308" strokeWidth="10" strokeLinecap="round" opacity="0.6" />
        <path d="M 148 42 A 80 80 0 0 1 180 100" fill="none" stroke="#22c55e" strokeWidth="10" strokeLinecap="round" opacity="0.6" />
        {/* Needle */}
        <line
          x1="100" y1="100"
          x2={100 + 60 * Math.cos((angle * Math.PI) / 180)}
          y2={100 - 60 * Math.sin((angle * Math.PI) / 180)}
          stroke={color} strokeWidth="2.5" strokeLinecap="round"
        />
        <circle cx="100" cy="100" r="4" fill={color} />
      </svg>
      <div className="text-center -mt-1">
        <span className="text-2xl font-data font-bold" style={{ color }}>{value}</span>
        <p className="text-[10px] font-medium mt-0.5" style={{ color }}>{classification}</p>
      </div>
    </div>
  );
}

function MiniChart({ data, color, height = 90, type = "area" }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !data || data.length === 0) return;
    if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: "#52525b", fontFamily: "'JetBrains Mono', monospace", fontSize: 9 },
      grid: { vertLines: { visible: false }, horzLines: { color: "rgba(63,63,70,0.1)" } },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.1, bottom: 0.05 } },
      timeScale: { borderVisible: false, fixLeftEdge: true, fixRightEdge: true },
      crosshair: { vertLine: { color: "rgba(59,130,246,0.2)", width: 1, style: 2, labelBackgroundColor: "#1e40af" }, horzLine: { color: "rgba(59,130,246,0.2)", width: 1, style: 2, labelBackgroundColor: "#1e40af" } },
      handleScroll: false, handleScale: false,
    });
    chartRef.current = chart;

    if (type === "histogram") {
      const series = chart.addSeries(HistogramSeries, { priceFormat: { type: "volume" } });
      series.setData(data.map(d => ({ time: d.date, value: d.value, color: d.color || color })));
    } else {
      const series = chart.addSeries(AreaSeries, { lineColor: color, topColor: `${color}20`, bottomColor: "transparent", lineWidth: 2 });
      series.setData(data.map(d => ({ time: d.date, value: d.value })));
    }
    chart.timeScale().fitContent();

    return () => { if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; } };
  }, [data, color, type]);

  if (!data || data.length === 0) return <div style={{ height }} className="flex items-center justify-center text-zinc-600 text-[10px]">No data</div>;
  return <div ref={containerRef} style={{ height }} />;
}

export default function MarketSentiment() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSentiment = async () => {
      try {
        const resp = await axios.get(`${API}/market-sentiment`);
        setData(resp.data);
      } catch (e) {
        console.error("Failed to fetch market sentiment:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchSentiment();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="sentiment-loading">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-[200px] bg-zinc-900/50" />)}
      </div>
    );
  }

  if (!data) return null;

  const fg = data.fear_greed;
  const btcDom = data.btc_dominance;
  const altS = data.alt_season;

  // Fear/Greed chart data with color-coded bars
  const fgChartData = fg?.history?.map(d => ({
    date: d.date,
    value: d.value,
    color: d.value >= 75 ? "rgba(34,197,94,0.5)" : d.value >= 50 ? "rgba(234,179,8,0.4)" : d.value >= 25 ? "rgba(249,115,22,0.4)" : "rgba(239,68,68,0.5)",
  })) || [];

  // Alt season gauge
  const altColor = altS?.score >= 75 ? "#10b981" : altS?.score >= 50 ? "#22c55e" : altS?.score >= 25 ? "#eab308" : "#f97316";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="market-sentiment">
      {/* Fear & Greed Index */}
      <div className="bg-zinc-900/40 border border-zinc-800/80 rounded-sm p-4" data-testid="fear-greed-panel">
        <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium mb-2">Fear & Greed Index</p>
        {fg ? (
          <div className="space-y-2">
            <FearGreedGauge value={fg.current_value} classification={fg.classification} />
            <MiniChart data={fgChartData} color="#3b82f6" height={70} type="histogram" />
            <p className="text-[9px] text-zinc-600 text-center">90-day history</p>
          </div>
        ) : (
          <p className="text-xs text-zinc-600 py-8 text-center">Unavailable</p>
        )}
      </div>

      {/* BTC Dominance */}
      <div className="bg-zinc-900/40 border border-zinc-800/80 rounded-sm p-4" data-testid="btc-dominance-panel">
        <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium mb-2">BTC Dominance</p>
        {btcDom ? (
          <div className="space-y-3">
            <div className="text-center">
              <span className="text-3xl font-data font-bold text-amber-400">{btcDom.btc}%</span>
              <p className="text-[10px] text-zinc-500 mt-0.5">of total crypto market cap</p>
            </div>
            {/* Dominance bar */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-zinc-500 w-10">BTC</span>
                <div className="flex-1 bg-zinc-800/50 rounded-sm h-3 overflow-hidden">
                  <div className="h-full bg-amber-500/70 rounded-sm transition-all" style={{ width: `${btcDom.btc}%` }} />
                </div>
                <span className="text-[10px] font-data text-amber-400 w-12 text-right">{btcDom.btc}%</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-zinc-500 w-10">ETH</span>
                <div className="flex-1 bg-zinc-800/50 rounded-sm h-3 overflow-hidden">
                  <div className="h-full bg-blue-500/70 rounded-sm transition-all" style={{ width: `${btcDom.eth}%` }} />
                </div>
                <span className="text-[10px] font-data text-blue-400 w-12 text-right">{btcDom.eth}%</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-zinc-500 w-10">Alts</span>
                <div className="flex-1 bg-zinc-800/50 rounded-sm h-3 overflow-hidden">
                  <div className="h-full bg-emerald-500/70 rounded-sm transition-all" style={{ width: `${btcDom.others}%` }} />
                </div>
                <span className="text-[10px] font-data text-emerald-400 w-12 text-right">{btcDom.others}%</span>
              </div>
            </div>
            <div className="flex justify-between pt-1 border-t border-zinc-800/40">
              <div>
                <p className="text-[9px] text-zinc-600">Total Market Cap</p>
                <p className="text-[11px] font-data text-zinc-300">
                  ${btcDom.total_market_cap ? (btcDom.total_market_cap / 1e12).toFixed(2) + "T" : "N/A"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[9px] text-zinc-600">24h Change</p>
                <p className={`text-[11px] font-data ${btcDom.market_cap_change_24h >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {btcDom.market_cap_change_24h >= 0 ? "+" : ""}{btcDom.market_cap_change_24h}%
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-xs text-zinc-600 py-8 text-center">Unavailable</p>
        )}
      </div>

      {/* Alt Season Index */}
      <div className="bg-zinc-900/40 border border-zinc-800/80 rounded-sm p-4" data-testid="alt-season-panel">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium">Alt Season Index</p>
          <a
            href="https://www.blockchaincenter.net/en/altcoin-season-index/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="alt-season-chart-link"
            className="text-[9px] text-cyan-500 hover:text-cyan-400 transition-colors flex items-center gap-1 px-1.5 py-0.5 border border-cyan-800/40 rounded-sm hover:bg-cyan-900/20"
          >
            BTC/Alt Chart <span className="text-[8px]">&#8599;</span>
          </a>
        </div>
        {altS ? (
          <div className="space-y-3">
            <div className="flex flex-col items-center">
              {/* Radial gauge */}
              <div className="relative">
                <svg viewBox="0 0 120 120" className="w-[130px] h-[130px]">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(63,63,70,0.2)" strokeWidth="8" />
                  <circle
                    cx="60" cy="60" r="50" fill="none"
                    stroke={altColor}
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 50}
                    strokeDashoffset={2 * Math.PI * 50 - (altS.score / 100) * 2 * Math.PI * 50}
                    transform="rotate(-90 60 60)"
                    className="transition-all duration-700"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-data font-bold" style={{ color: altColor }}>{altS.score}</span>
                  <span className="text-[9px] text-zinc-500">/100</span>
                </div>
              </div>
              <p className="text-xs font-medium mt-1" style={{ color: altColor }}>{altS.label}</p>
            </div>
            <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-3 space-y-2">
              <div className="flex justify-between text-[10px]">
                <span className="text-zinc-500">BTC Dominance</span>
                <span className="font-data text-zinc-300">{altS.btc_dominance}%</span>
              </div>
              <div className="w-full bg-zinc-800/50 rounded-sm h-2">
                <div
                  className="h-full rounded-sm transition-all"
                  style={{
                    width: `${altS.score}%`,
                    background: `linear-gradient(90deg, #f97316, ${altColor})`
                  }}
                />
              </div>
              <div className="flex justify-between text-[8px] text-zinc-600">
                <span>BTC Season</span>
                <span>Alt Season</span>
              </div>
            </div>
            <p className="text-[9px] text-zinc-600 text-center leading-relaxed">
              {altS.score >= 75 ? "Altcoins are significantly outperforming Bitcoin" :
               altS.score >= 50 ? "Market is leaning toward altcoin strength" :
               altS.score >= 25 ? "Market is balanced between BTC and alts" :
               "Bitcoin is dominating the crypto market"}
            </p>
          </div>
        ) : (
          <p className="text-xs text-zinc-600 py-8 text-center">Unavailable</p>
        )}
      </div>
    </div>
  );
}
